import { Card, CardContent, Switch, Typography } from '@mui/material';
import { FunctionComponent } from 'react';
import { Control, Controller } from 'react-hook-form';
import { UserFormModel } from '../UserFormModel';
import { Permission } from '@/modules/users/submodules/roles/api/permissions.contracts';
import { useTranslation } from '@/lib';
import { useAuthUserContext } from '@/modules/auth/context/AuthUserContext';

interface GuestPermissionsCardProps {
  control: Control<UserFormModel>;
}

type GuestPermissionItem = {
  name: string;
  description: string;
  permission: Permission;
};

export const GuestPermissionsCard: FunctionComponent<GuestPermissionsCardProps> = ({ control }) => {
  const { t } = useTranslation();
  const { isBetaTenant } = useAuthUserContext();

  const showTransactions = isBetaTenant();

  const guestPermissions: GuestPermissionItem[] = [
    {
      name: t('view_locations'),
      description: t('allows_user_to_view_locations'),
      permission: Permission.LOCATIONS_VIEW,
    },
    ...(showTransactions
      ? [
          {
            name: t('view_transactions'),
            description: t('allows_user_to_view_transactions'),
            permission: Permission.TRANSACTIONS_VIEW,
          },
          {
            name: t('edit_transactions'),
            description: t('allows_guest_to_create_edit_transactions_in_assigned_locations'),
            permission: Permission.TRANSACTIONS_EDIT,
          },
        ]
      : []),
  ];

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" className="pb-2" color="textSecondary">
          {t('permissions')}
        </Typography>
        <Typography variant="body2" className="pb-3" color="textSecondary">
          {t('guest_permissions_description')}
        </Typography>

        <Controller
          name="permissions"
          control={control}
          render={({ field }) => {
            const selectedPermissions = field.value || [];

            const togglePermission = (permission: Permission) => {
              const newPermissions = selectedPermissions.includes(permission)
                ? selectedPermissions.filter((p) => p !== permission)
                : [...selectedPermissions, permission];
              field.onChange(newPermissions);
            };

            return (
              <div className="grid rounded-md border overflow-hidden border-gray-200 dark:border-gray-600">
                {/* Header row */}
                <div className="grid" style={{ gridTemplateColumns: '1fr 100px' }}>
                  <div className="uppercase text-xs font-semibold border-gray-200 bg-gray-100 border-b dark:border-gray-600 dark:bg-gray-800 h-9 items-center px-3 flex">
                    {t('actions')}
                  </div>
                  <div className="uppercase text-xs font-semibold border-gray-200 bg-gray-100 border-b border-l dark:border-gray-600 dark:bg-gray-800 h-9 items-center justify-center flex">
                    {t('enabled')}
                  </div>
                </div>

                {/* Permission rows */}
                {guestPermissions.map((permissionItem, index) => (
                  <div key={permissionItem.permission} className="grid" style={{ gridTemplateColumns: '1fr 100px' }}>
                    <div
                      className={`px-3 py-3 ${index < guestPermissions.length - 1 ? 'border-b border-gray-200 dark:border-gray-600' : ''}`}
                    >
                      <div className="text-base font-medium">{permissionItem.name}</div>
                      <div className="text-gray-700 text-xs dark:text-gray-400 pt-0.5">{permissionItem.description}</div>
                    </div>
                    <div
                      className={`p-2 border-l flex justify-center items-center border-gray-200 dark:border-gray-600 ${
                        index < guestPermissions.length - 1 ? 'border-b' : ''
                      }`}
                    >
                      <Switch
                        color="primary"
                        checked={selectedPermissions.includes(permissionItem.permission)}
                        onChange={() => togglePermission(permissionItem.permission)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            );
          }}
        />
      </CardContent>
    </Card>
  );
};
